import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
    GetAllEmployeeExits,
    GetEmployeeExitByID,
    CreateEmployeeExit,
    UpdateEmployeeExit,
    ApproveEmployeeExit,
    ConductExitInterview,
    UpdateFinalSettlement,
    DeactivateEmployeeAccount,
    CompleteEmployeeExit,
    AddExitChecklistItem,
    UpdateExitChecklistItem,
    DeleteExitChecklistItem
} from "helpers/api_method";
import { extractArrayFromResponse, extractDataFromResponse } from "utils/responseHelpers";

export const getAllEmployeeExits = createAsyncThunk(
    "exitManagement/getAllEmployeeExits",
    async (params: { employee_id?: string; exit_status?: string; exit_type?: string } = {}, { rejectWithValue }) => {
        try {
            const response = await GetAllEmployeeExits(params.employee_id, params.exit_status, params.exit_type);
            const data = extractArrayFromResponse(response);
            return data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch exit records');
        }
    }
);

export const getEmployeeExitByID = createAsyncThunk(
    "exitManagement/getEmployeeExitByID",
    async (exit_id: string, { rejectWithValue }) => {
        try {
            const response = await GetEmployeeExitByID(exit_id);
            const data = extractDataFromResponse(response);
            return data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch exit record');
        }
    }
);

export const createEmployeeExit = createAsyncThunk(
    "exitManagement/createEmployeeExit",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await CreateEmployeeExit(data);
            const exitData = extractDataFromResponse(response);
            toast.success("Exit process initiated successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create exit record';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateEmployeeExit = createAsyncThunk(
    "exitManagement/updateEmployeeExit",
    async ({ exit_id, data }: { exit_id: string; data: any }, { rejectWithValue }) => {
        try {
            const response = await UpdateEmployeeExit(exit_id, data);
            const exitData = extractDataFromResponse(response);
            toast.success("Exit record updated successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update exit record';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const approveEmployeeExit = createAsyncThunk(
    "exitManagement/approveEmployeeExit",
    async ({ exit_id, data }: { exit_id: string; data?: any }, { rejectWithValue }) => {
        try {
            const response = await ApproveEmployeeExit(exit_id, data || {});
            const exitData = extractDataFromResponse(response);
            toast.success("Exit request approved successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to approve exit request';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const conductExitInterview = createAsyncThunk(
    "exitManagement/conductExitInterview",
    async ({ exit_id, data }: { exit_id: string; data: any }, { rejectWithValue }) => {
        try {
            const response = await ConductExitInterview(exit_id, data);
            const exitData = extractDataFromResponse(response);
            toast.success("Exit interview recorded successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to record exit interview';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateFinalSettlement = createAsyncThunk(
    "exitManagement/updateFinalSettlement",
    async ({ exit_id, data }: { exit_id: string; data: any }, { rejectWithValue }) => {
        try {
            const response = await UpdateFinalSettlement(exit_id, data);
            const exitData = extractDataFromResponse(response);
            toast.success("Final settlement updated successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update final settlement';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const deactivateEmployeeAccount = createAsyncThunk(
    "exitManagement/deactivateEmployeeAccount",
    async (exit_id: string, { rejectWithValue }) => {
        try {
            const response = await DeactivateEmployeeAccount(exit_id);
            const exitData = extractDataFromResponse(response);
            toast.success("Employee account deactivated successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to deactivate account';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const completeEmployeeExit = createAsyncThunk(
    "exitManagement/completeEmployeeExit",
    async (exit_id: string, { rejectWithValue }) => {
        try {
            const response = await CompleteEmployeeExit(exit_id);
            const exitData = extractDataFromResponse(response);
            toast.success("Exit process completed successfully");
            return exitData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to complete exit process';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const addExitChecklistItem = createAsyncThunk(
    "exitManagement/addExitChecklistItem",
    async ({ exit_id, data }: { exit_id: string; data: any }, { rejectWithValue }) => {
        try {
            const response = await AddExitChecklistItem(exit_id, data);
            const itemData = extractDataFromResponse(response);
            toast.success("Checklist item added successfully");
            return itemData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to add checklist item';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateExitChecklistItem = createAsyncThunk(
    "exitManagement/updateExitChecklistItem",
    async ({ checklist_id, data }: { checklist_id: string; data: any }, { rejectWithValue }) => {
        try {
            const response = await UpdateExitChecklistItem(checklist_id, data);
            const itemData = extractDataFromResponse(response);
            toast.success("Checklist item updated successfully");
            return itemData;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update checklist item';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteExitChecklistItem = createAsyncThunk(
    "exitManagement/deleteExitChecklistItem",
    async (checklist_id: string, { rejectWithValue }) => {
        try {
            await DeleteExitChecklistItem(checklist_id);
            toast.success("Checklist item deleted successfully");
            return checklist_id;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete checklist item';
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

